<ng-container *transloco="let t">
  <ld-base-modal
    [title]="t('modalsSelectAccountsTitle')"
    [size]="'large'"
    [closeOnBackdropClick]="!isLoading()"
    (closeModal)="onCancel()"
  >
    <div modal-content>
      <p class="font-dm text-sm text-ld-neutral-500 mt-1">
        {{ t('modalsSelectAccountsSubtitle') }}
      </p>

      @if (errorMessage()) {
        <div class="flex flex-col items-center justify-center py-8 text-center">
          <div class="size-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
            <img [src]="errorIcon" alt="Error" class="w-6 h-6" />
          </div>
          <h3 class="font-dm font-semibold text-base text-ld-neutral mb-1">
            {{ t('modalsSelectAccountsErrorTitle') }}
          </h3>
          <p class="font-dm text-sm text-ld-neutral-500 mb-4">{{ errorMessage() }}</p>
          <button
            id="select-accounts-try-again-btn"
            type="button"
            class="px-4 py-2 text-sm font-dm font-semibold text-ld-primary hover:bg-ld-primary-50 rounded-lg transition-colors cursor-pointer"
            (click)="onBack()"
          >
            {{ t('modalsSelectAccountsTryDifferentBank') }}
          </button>
        </div>
      } @else if (!hasAccounts()) {
        <div class="flex flex-col items-center justify-center py-8 text-center">
          <div class="size-12 rounded-full bg-ld-neutral-100 flex items-center justify-center mb-3">
            <mat-icon class="text-ld-neutral-400 text-2xl!">account_balance</mat-icon>
          </div>
          <h3 class="font-dm font-semibold text-base text-ld-neutral mb-1">
            {{ t('modalsSelectAccountsNoAccountsTitle') }}
          </h3>
          <p class="font-dm text-sm text-ld-neutral-500 mb-4">
            {{ t('modalsSelectAccountsNoAccountsMessage') }}
          </p>
          <button
            id="select-accounts-back-btn"
            type="button"
            class="px-4 py-2 text-sm font-dm font-semibold text-ld-primary hover:bg-ld-primary-50 rounded-lg transition-colors cursor-pointer"
            (click)="onBack()"
          >
            {{ t('modalsSelectAccountsSelectDifferentBank') }}
          </button>
        </div>
      } @else {
        <div class="flex items-center justify-between mb-3">
          <p class="font-dm text-sm text-ld-neutral-500">
            {{
              t('modalsSelectAccountsSelectedCount', {
                selected: selectedAccountIds().size,
                total: accounts().length,
              })
            }}
          </p>
          <div class="flex gap-2">
            <button
              id="select-accounts-select-all-btn"
              type="button"
              class="text-sm font-dm font-medium text-ld-primary hover:underline cursor-pointer"
              (click)="selectAll()"
              [disabled]="isLoading()"
            >
              {{ t('commonSelectAccountsSelectAll') }}
            </button>
            <span class="text-ld-neutral-300">|</span>
            <button
              id="select-accounts-deselect-all-btn"
              type="button"
              class="text-sm font-dm font-medium text-ld-primary hover:underline cursor-pointer"
              (click)="deselectAll()"
              [disabled]="isLoading()"
            >
              {{ t('modalsSelectAccountsDeselectAll') }}
            </button>
          </div>
        </div>

        <div class="space-y-2">
          @for (account of accounts(); track account.accountId; let i = $index) {
            <button
              [id]="'account-option-' + i"
              type="button"
              class="w-full flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all border-2"
              [class.border-ld-primary]="isSelected(account)"
              [class.border-ld-neutral-200]="!isSelected(account)"
              [class.hover:border-ld-neutral-300]="!isSelected(account)"
              [class.hover:bg-ld-neutral-50]="!isSelected(account)"
              (click)="toggleAccount(account)"
              [disabled]="isLoading()"
            >
              <div
                class="size-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
                [class.border-ld-primary]="isSelected(account)"
                [class.bg-ld-primary]="isSelected(account)"
                [class.border-ld-neutral-300]="!isSelected(account)"
              >
                @if (isSelected(account)) {
                  <mat-icon class="text-white text-sm! w-3! h-3!">check</mat-icon>
                }
              </div>

              <div class="size-10 rounded-lg flex items-center justify-center shrink-0 bg-ld-neutral-100">
                <mat-icon
                  class="text-xl!"
                  [class.text-ld-primary]="isSelected(account)"
                  [class.text-ld-neutral-500]="!isSelected(account)"
                >
                  account_balance
                </mat-icon>
              </div>

              <div class="flex-1 text-left min-w-0">
                <p
                  class="font-dm text-sm font-medium truncate"
                  [class.text-ld-primary]="isSelected(account)"
                  [class.text-ld-neutral]="!isSelected(account)"
                >
                  {{ account.nickName || account.accountNumber || t('commonSelectAccountsAccountFallback') }}
                </p>
                <p class="font-dm text-xs text-ld-neutral-500">
                  {{ account.institutionName || t('commonSelectAccountsAccountFallback') }}
                  @if (account.accountNumber) {
                    <span class="ml-1">•••{{ account.accountNumber | slice: -4 }}</span>
                  }
                </p>
              </div>

              <div class="text-right shrink-0">
                <p
                  class="font-dm text-sm font-semibold"
                  [class.text-ld-primary]="isSelected(account)"
                  [class.text-ld-neutral]="!isSelected(account)"
                >
                  {{ account.balance | currency: 'USD' : 'symbol' : '1.2-2' }}
                </p>
              </div>
            </button>
          }
        </div>

        @if (hasSelection()) {
          <div class="mt-4 pt-4 border-t border-ld-neutral-200 flex items-center justify-between">
            <p class="font-dm text-sm text-ld-neutral-500">{{ t('modalsSelectAccountsSelectedCount') }}</p>
            <p class="font-dm text-lg font-bold text-ld-neutral">
              {{ totalSelectedBalance() | currency: 'USD' : 'symbol' : '1.2-2' }}
            </p>
          </div>
        }
      }
    </div>
    <div modal-footer>
      <button
        id="select-accounts-back-btn"
        type="button"
        class="btn-outline-ld-purple"
        [disabled]="isLoading()"
        (click)="onBack()"
      >
        <mat-icon class="text-lg! w-4! h-4!">arrow_back</mat-icon>
        {{ t('modalsSelectAccountsDifferentBank') }}
      </button>
      <button
        id="select-accounts-cancel-btn"
        type="button"
        class="btn-outline-ld-purple"
        [disabled]="isLoading()"
        (click)="onCancel()"
      >
        {{ t('commonButtonsCancel') }}
      </button>
      <button
        id="select-accounts-confirm-btn"
        type="button"
        class="btn-primary-ld-purple"
        [disabled]="isLoading() || !hasSelection()"
        (click)="onConfirm()"
      >
        @if (isLoading()) {
          <mat-spinner diameter="16" class="w-4! h-4! [&_circle]:stroke-white!"></mat-spinner>
          {{ t('modalsSelectAccountsLinking') }}
        } @else {
          {{ selectedAccountIds().size === 1 ? t('commonMessagesAddAccount') : t('modalsSelectAccountsAddAccounts') }}
        }
      </button>
    </div>
  </ld-base-modal>
</ng-container>
